import { Injectable, Injector } from '@angular/core';
import { WorkOrderControllerService } from '@app/generated/service';
import { DocumentDto, DocumentLiveStatusDto, DocumentStatusDto } from '@app/generated/model';
import { AbstractWorkOrderDataService } from '@app/work-order/abstract-work-order-data.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { WorkOrderFile } from '@app/core/db/offline-data';
import { getDocumentUrl } from './url-helper';
import { firstValueFrom, Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ChangeLogEvent } from '@app/core/db/change-log';
import { WO_UPDATE_DOCUMENT_PROCESS_STATUS } from '@app/actions';
import { DownloadStatus, isDocumentSelected, WorkOrderDownloadOptions } from '@app/core/model/document-download';
import { FeatureFlagService } from '@app/core/services/feature-flag.service';
import { FeatureFlagKey } from '@dwo/enums';
import { NetworkStatusService } from '@app/core/services/network-status.service';

const MAX_PARALLEL_DOWNLOADS = 3;

interface UpdateDocumentProcessStatus {
  workOrderId: string;
  documentId: string;
  documentProcessStatus: number;
}

@Injectable({
  providedIn: 'root',
})
export class WorkOrderDocumentService extends AbstractWorkOrderDataService<DocumentDto[]> {
  private readonly livePendingIds = new Set<string>();
  private readonly liveErrorIds = new Set<string>();
  private liveRefreshInFlight: Promise<void> | null = null;
  // The No/name for a given doc_process_status_id are project-specific (defined in dwo_document_process_status) -
  // never hardcode them; this cache is warmed on load() and kept current by getDocumentProcessStatuses().
  private statusCatalogue: DocumentStatusDto[] = [];

  constructor(
    private workOrderController: WorkOrderControllerService,
    private httpClient: HttpClient,
    private featureFlagService: FeatureFlagService,
    private networkStatusService: NetworkStatusService,
    injector: Injector,
  ) {
    super(injector, 'DOCUMENTS');
  }

  protected load(): void {
    if (!this.workOrderId) {
      return;
    }

    this.workOrderController.getWorkOrderDocuments(this.workOrderId).subscribe({
      next: (data) => {
        this.setData(data);
        this.refreshStatusCatalogue();
        this.refreshLiveStatuses();
      },
      error: (error) => {
        console.warn(`[DOCUMENTS] Failed to load documents for ${this.workOrderId}.`, error);
        void this.handleLoadFailure([], error);
      },
    });
  }

  public async download(options?: WorkOrderDownloadOptions) {
    const workOrderId = this.workOrderId;
    const documents = await this.resolveDocumentsForDownload();
    this.setData(documents);
    // Bake the latest MIPS status into the metadata snapshot that's always downloaded, before any file content.
    await this.refreshLiveStatusesAndWait();
    await super.download(options);

    if (!workOrderId) {
      return;
    }

    const selection = options?.selection;
    const downloadable = this.showTimeDocuments()
      ? documents
      : documents.filter((document) => document.documentType !== DocumentDto.DocumentTypeEnum.Link);
    const selected = selection
      ? downloadable.filter((document) => isDocumentSelected(document, selection))
      : downloadable;
    const onStatus = options?.onDocumentStatus ?? (() => undefined);
    selected.forEach((document) => onStatus(document.id ?? '', 'queued'));

    // A small worker pool: one slow document (e.g. a Showfile2-backed link) must not block the rest,
    // and tablets shouldn't hold every file in memory at once.
    const queue = [...selected];
    const worker = async () => {
      for (let document = queue.shift(); document; document = queue.shift()) {
        await this.downloadDocument(workOrderId, document, onStatus);
      }
    };
    await Promise.all(Array.from({ length: Math.min(MAX_PARALLEL_DOWNLOADS, queue.length) }, worker));
  }

  private showTimeDocuments(): boolean {
    const projectId = (this.getParent() as { projectId?: string } | null)?.projectId;
    return this.featureFlagService.isEnabled(FeatureFlagKey.ShowTimeDocuments, projectId);
  }

  private async resolveDocumentsForDownload(): Promise<DocumentDto[]> {
    if (!this.workOrderId) {
      return [];
    }

    try {
      const docs = await firstValueFrom(this.workOrderController.getWorkOrderDocuments(this.workOrderId));
      return Array.isArray(docs) ? docs : [];
    } catch (error) {
      console.warn(
        '[DOCUMENTS] Failed to refresh documents list for offline download. Using existing/empty list.',
        error,
      );
      const current = this.get();
      return Array.isArray(current) ? current : [];
    }
  }

  private async downloadDocument(
    workOrderId: string,
    document: DocumentDto,
    onStatus: (documentId: string, status: DownloadStatus) => void,
  ) {
    const documentId = document.id ?? '';
    onStatus(documentId, 'downloading');
    const url = document.link ? getDocumentUrl(workOrderId, document) : null;
    const success = await this.downloadFile(workOrderId, url).catch((error) => {
      // e.g. QuotaExceededError when IndexedDB is full
      console.warn(`[DOCUMENTS] Failed to store file: ${url}`, error);
      return false;
    });
    onStatus(documentId, success ? 'done' : 'failed');
  }

  private async downloadFile(workOrderId: string, url: string | null): Promise<boolean> {
    if (!url) {
      return false;
    }

    if (await this.hasFile(url)) {
      return true;
    }

    const blob = await firstValueFrom(this.httpClient.get(url, { responseType: 'blob' })).catch((error) => {
      const httpError = error as HttpErrorResponse;
      if (httpError?.status === 404) {
        console.warn(`[DOCUMENTS] Missing file (404), skipping offline download: ${url}`);
      } else {
        console.warn(`[DOCUMENTS] Failed to download file, skipping: ${url}`, error);
      }
      return null;
    });

    // The global HTTP error interceptor can re-emit failures as values instead of errors.
    if (!(blob instanceof Blob)) {
      return false;
    }

    const file: WorkOrderFile = {
      workOrderId,
      url,
      data: await blob.arrayBuffer(),
      type: blob.type,
    };

    await this.offlineData.workOrderFiles.put(file);
    return true;
  }

  private async hasFile(url: string) {
    const numberOfFiles = await this.offlineData.workOrderFiles.where({ url }).count();
    return numberOfFiles > 0;
  }

  public updateDocumentProcessStatus(documentId: string, statusCode: number): Promise<string> {
    const payload: UpdateDocumentProcessStatus = {
      workOrderId: this.workOrderId,
      documentId,
      documentProcessStatus: statusCode,
    };
    return this.changeLogService.publish(WO_UPDATE_DOCUMENT_PROCESS_STATUS, payload, [this.workOrderId]);
  }

  // Fetched separately from the document list itself (perf/error-isolation for the status picker);
  // also keeps the catalogue cache used by apply() current.
  public getDocumentProcessStatuses(): Observable<DocumentStatusDto[]> {
    return this.workOrderController.getWorkOrderDocumentProcessStatuses(this.workOrderId).pipe(
      tap((statuses) => {
        if (Array.isArray(statuses)) {
          this.statusCatalogue = statuses;
        }
      }),
    );
  }

  // Fire-and-forget cache warm-up so apply() can resolve a real name even for changes replayed
  // before the status dialog has ever been opened in this tab.
  private refreshStatusCatalogue(): void {
    if (!this.workOrderId) {
      return;
    }
    this.getDocumentProcessStatuses().subscribe({
      error: (error) => console.warn('[DOCUMENTS] Failed to load the document status catalogue.', error),
    });
  }

  public isLiveStatusPending(documentId: string | null | undefined): boolean {
    return !!documentId && this.livePendingIds.has(documentId);
  }

  // True only when the backend's MIPS fetch for this document actually failed (vs. a benign null status).
  public hasLiveStatusError(documentId: string | null | undefined): boolean {
    return !!documentId && this.liveErrorIds.has(documentId);
  }

  /** Fire-and-forget background trigger (work order just opened); fallback DWO values stay shown until this resolves. */
  public refreshLiveStatuses(): void {
    void this.ensureLiveStatusesRefreshed();
  }

  /** Awaited variant so an offline download bakes the live status into the downloaded snapshot. */
  public async refreshLiveStatusesAndWait(): Promise<void> {
    await this.ensureLiveStatusesRefreshed();
  }

  // Shared in-flight promise: load() and download() can both trigger a refresh back-to-back.
  private ensureLiveStatusesRefreshed(): Promise<void> {
    if (!this.liveRefreshInFlight) {
      this.liveRefreshInFlight = this.doRefreshLiveStatuses().finally(() => {
        this.liveRefreshInFlight = null;
      });
    }
    return this.liveRefreshInFlight;
  }

  private async doRefreshLiveStatuses(): Promise<void> {
    if (!this.workOrderId) {
      return;
    }

    const projectId = (this.getParent() as { projectId?: string } | null)?.projectId;
    if (!this.featureFlagService.isEnabled(FeatureFlagKey.EnableDocumentProcessStatus, projectId)) {
      return;
    }

    const online = await firstValueFrom(this.networkStatusService.isOnline());
    if (!online) {
      return;
    }

    const documents = this.get() ?? [];
    const ids = documents.map((doc) => doc.id).filter((id): id is string => !!id);
    if (ids.length === 0) {
      return;
    }

    ids.forEach((id) => this.livePendingIds.add(id));
    try {
      const liveStatuses = await firstValueFrom(
        this.workOrderController.getWorkOrderDocumentLiveStatuses(this.workOrderId),
      );
      // The global HTTP error interceptor can re-emit failures as values instead of errors.
      this.applyLiveStatuses(Array.isArray(liveStatuses) ? liveStatuses : []);
    } catch (error) {
      console.warn('[DOCUMENTS] Failed to refresh live document statuses from MIPS.', error);
    } finally {
      ids.forEach((id) => this.livePendingIds.delete(id));
    }
  }

  private applyLiveStatuses(liveStatuses: DocumentLiveStatusDto[]): void {
    const byId = new Map(
      liveStatuses.filter((status) => status.documentId).map((status) => [status.documentId as string, status]),
    );

    const failedIds = liveStatuses
      .filter((status) => status.error && status.documentId)
      .map((status) => status.documentId as string);
    failedIds.forEach((id) => this.liveErrorIds.add(id));
    byId.forEach((status, id) => {
      if (!status.error) {
        this.liveErrorIds.delete(id);
      }
    });
    if (failedIds.length > 0) {
      console.warn(`[DOCUMENTS] MIPS live-status fetch failed for ${failedIds.length} document(s).`, failedIds);
    }

    const documents = this.get() ?? [];
    const updated = documents.map((doc) => {
      const liveStatus = doc.id ? byId.get(doc.id)?.status : undefined;
      return liveStatus ? { ...doc, documentProcessStatus: liveStatus } : doc;
    });
    this.setData(updated);
  }

  protected apply(documents: DocumentDto[], event: ChangeLogEvent) {
    if (event.type !== WO_UPDATE_DOCUMENT_PROCESS_STATUS || !Array.isArray(documents)) {
      return;
    }

    const change = event.content as UpdateDocumentProcessStatus;
    if (!documents.some((doc) => doc.id === change.documentId)) {
      return;
    }

    // Resolve the real per-project No/name from the catalogue - it is not a fixed/universal mapping.
    const matchedStatus = this.statusCatalogue.find((status) => status.id === change.documentProcessStatus);

    // New array/object references required: setData()'s distinctUntilChanged compares by reference.
    const updated = documents.map((doc) =>
      doc.id === change.documentId
        ? {
            ...doc,
            documentProcessStatus: matchedStatus ?? { id: change.documentProcessStatus },
          }
        : doc,
    );
    this.setData(updated);
  }
}
